import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";

import { APP_API_BASE } from "../../../config/api";
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  `${APP_API_BASE}`;

export default function DeviceInventoryReconciliationDialog({
  open,
  onClose,
  activeOrg,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState({});
  const [bindingDeviceId, setBindingDeviceId] = useState("");

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const loadOrphanDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(
        `${API_BASE}/api/v1/inventory/reconciliation/orphan-devices`,
        { headers },
      );
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setRows([]);
      setError(
        err?.response?.data?.error ||
          "Failed to load device-inventory reconciliation data",
      );
    } finally {
      setLoading(false);
    }
  }, [activeOrg?.id]);

  useEffect(() => {
    if (open) {
      setSuccess("");
      setSelectedAssets({});
      setBindingDeviceId("");
      loadOrphanDevices();
    }
  }, [open, loadOrphanDevices]);

  const handleBind = async (deviceId) => {
    const inventoryAssetId = selectedAssets[deviceId];
    if (!inventoryAssetId) return;

    try {
      setBindingDeviceId(deviceId);
      setError("");
      setSuccess("");
      await axios.post(
        `${API_BASE}/api/v1/inventory/bindings`,
        {
          device_id: deviceId,
          inventory_asset_id: inventoryAssetId,
        },
        { headers },
      );

      setSuccess("Existing device successfully linked to inventory asset.");
      setSelectedAssets((prev) => {
        const next = { ...prev };
        delete next[deviceId];
        return next;
      });
      await loadOrphanDevices();
      onSuccess?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to bind device to selected inventory asset",
      );
    } finally {
      setBindingDeviceId("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 5,
          border: "1px solid rgba(148,163,184,0.18)",
          boxShadow: "0 30px 90px rgba(15, 23, 42, 0.22)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 800, fontSize: 28, color: "#0F172A" }}>
            Device Inventory Reconciliation
          </Typography>
          <Typography sx={{ color: "#64748B" }}>
            Review older devices that do not yet have an active inventory asset
            binding, then map them to available in-stock assets from the same
            organization.
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "#F8FAFC" }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" sx={{ mb: 2.5 }}>
            {success}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid rgba(148,163,184,0.18)",
              backgroundColor: "#FFFFFF",
            }}
          >
            <CardContent sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                Everything is in sync
              </Typography>
              <Typography sx={{ mt: 1, color: "#64748B" }}>
                No orphaned devices were found for the current workspace.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {rows.map((entry) => {
              const device = entry.device;
              const selectedAssetId = selectedAssets[device.id] || "";

              return (
                <Card
                  key={device.id}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: "1px solid rgba(148,163,184,0.18)",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} lg={4}>
                        <Stack spacing={1}>
                          <Typography
                            sx={{ fontWeight: 800, color: "#0F172A" }}
                          >
                            {device.name}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Chip
                              label={device.organization?.name || "Unknown Org"}
                              sx={{
                                borderRadius: 999,
                                fontWeight: 700,
                                backgroundColor: "#E2E8F0",
                              }}
                            />
                            <Chip
                              label={
                                device.device_type?.name || "Unclassified Type"
                              }
                              sx={{
                                borderRadius: 999,
                                fontWeight: 700,
                                color: "#1D4ED8",
                                backgroundColor: "#DBEAFE",
                              }}
                            />
                          </Stack>
                          <Typography sx={{ color: "#64748B", fontSize: 13 }}>
                            Device ID: {device.id}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} lg={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Inventory Asset</InputLabel>
                          <Select
                            label="Select Inventory Asset"
                            value={selectedAssetId}
                            onChange={(e) =>
                              setSelectedAssets((prev) => ({
                                ...prev,
                                [device.id]: e.target.value,
                              }))
                            }
                          >
                            <MenuItem value="">
                              <em>Select matching asset</em>
                            </MenuItem>
                            {entry.candidate_assets.map((asset) => (
                              <MenuItem key={asset.id} value={asset.id}>
                                {asset.serial_number} | {asset.asset_type} |{" "}
                                {asset.model}
                                {asset.match_reasons?.includes(
                                  "device_id_qr_match",
                                )
                                  ? " | Exact device match"
                                  : ""}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography
                          sx={{ mt: 1, color: "#64748B", fontSize: 13 }}
                        >
                          {entry.candidate_count} ranked in-stock candidate
                          asset{entry.candidate_count === 1 ? "" : "s"} found
                          using org, device type, and device-id matching.
                        </Typography>
                      </Grid>

                      <Grid item xs={12} lg={3}>
                        <Stack alignItems={{ xs: "stretch", lg: "flex-end" }}>
                          <Button
                            variant="contained"
                            startIcon={
                              bindingDeviceId === device.id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <LinkRoundedIcon />
                              )
                            }
                            disabled={
                              !selectedAssetId || bindingDeviceId === device.id
                            }
                            onClick={() => handleBind(device.id)}
                            sx={{
                              minWidth: 180,
                              borderRadius: 999,
                              textTransform: "none",
                              fontWeight: 700,
                            }}
                          >
                            {bindingDeviceId === device.id
                              ? "Binding..."
                              : "Bind Asset"}
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          startIcon={<AutorenewRoundedIcon />}
          onClick={loadOrphanDevices}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Refresh
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
